// ============================================================
// govemCompensationEngine.js
// เครื่องคำนวณการเลื่อนค่าตอบแทนพนักงานราชการ (pure functions, ไม่ผูก Vue)
//
// พอร์ตจาก college/gov_em/cal_compensation.vue (buildRecord → calculateCompensation
// → applyRoundingAndBudgetControl) ให้ "หลักการเดียวกับสถานศึกษา" ทุกขั้นตอน
// ใช้โดยหน้าส่วนกลาง administrator/gov_em/central_compensation.vue
//
// ส่วนที่ต่างจากหน้าสถานศึกษาโดยตั้งใจ (ป้องกันข้อมูลผิดพลาด):
//   1) ค่าตอบแทน ≤ 0 → ไม่มีสิทธิ์ (หน้าเดิมให้มีสิทธิ์แล้วได้ % เป็น NaN)
//   2) ค่าตอบแทนเดิม ≥ ขั้นสูงกลุ่มงาน → เลื่อน 0 บาท + ถึงเพดาน
//      (หน้าเดิมคำนวณเงินเลื่อนติดลบ)
//   3) หลังขั้นกระจายวงเงินคงเหลือ ตัดรายที่เกิน maxPct% (ปัดขึ้นหลักสิบ) ข้อ 1(2)
//      (หน้าเดิมตรวจแค่ขั้นสูงกลุ่มงาน ทำให้สถานศึกษาที่มีผู้ไม่มีสิทธิ์มาก บางรายได้เกิน 6%)
//   4) เลือกรอบผลประเมินแบบ latest ได้ (รอบ 2 ถ้ามี ไม่มีใช้รอบ 1)
// ============================================================

// ขั้นสูงกลุ่มงาน ตามประกาศ พ.ศ. 2568 (แนบท้าย) — ต้องตรงกับหน้าสถานศึกษา
export const GROUP_MAX_SALARY = {
  บริการ: 20210,
  เทคนิคทั่วไป: 24930,
  เทคนิคพิเศษ: 59790,
  บริหารทั่วไป: 34700,
  วิชาชีพเฉพาะ: 44550,
  เชี่ยวชาญเฉพาะ: 68350
};
const GROUP_KEY_MAP = {
  บริการ: "บริการ",
  เทคนิค: "เทคนิคทั่วไป",
  เทคนิคทั่วไป: "เทคนิคทั่วไป",
  เทคนิคพิเศษ: "เทคนิคพิเศษ",
  บริหารทั่วไป: "บริหารทั่วไป",
  วิชาชีพเฉพาะ: "วิชาชีพเฉพาะ",
  เชี่ยวชาญเฉพาะ: "เชี่ยวชาญเฉพาะ"
};

export const DEFAULT_PARAMS = {
  budgetPct: 4.0, // วงเงินรวม ≤ 4% ข้อ 1(3)
  maxPct: 6.0, // สูงสุดรายบุคคล ≤ 6% ข้อ 1(2)
  roundMode: "latest" // "1" | "2" | "latest"
};

// ปัดเศษ: เศษไม่ถึง 10 บาท → ปรับขึ้น 10 บาท (ข้อ 1(6))
export function roundUpTen(val) {
  const r = val % 10;
  return r === 0 ? val : val - r + 10;
}

export function getGroupMax(groupName) {
  return GROUP_MAX_SALARY[GROUP_KEY_MAP[groupName] || "บริหารทั่วไป"] || 34700;
}

export function gradeLabel(score) {
  if (!score || score <= 0) return "ไม่มีข้อมูล";
  if (score >= 90) return "ดีเด่น";
  if (score >= 80) return "ดีมาก";
  if (score >= 70) return "ดี";
  if (score >= 60) return "พอใช้";
  return "ต้องปรับปรุง";
}

/** เลือกคะแนนตามรอบ คืน { score, round, status } */
export function pickScore(emp, roundMode) {
  const s1 = parseFloat(emp.score_r1 || 0);
  const s2 = parseFloat(emp.score_r2 || 0);
  if (roundMode === "1") return { score: s1, round: s1 > 0 ? 1 : null, status: emp.status_r1 || null };
  if (roundMode === "2") return { score: s2, round: s2 > 0 ? 2 : null, status: emp.status_r2 || null };
  if (s2 > 0) return { score: s2, round: 2, status: emp.status_r2 || null };
  return { score: s1, round: s1 > 0 ? 1 : null, status: emp.status_r1 || null };
}

/**
 * สร้าง record + ตรวจสิทธิ์ (เทียบ buildRecord ของหน้าสถานศึกษา)
 * emp = แถวจาก govem_compensation_central.php?action=college_data
 */
export function buildRecord(emp, params, order) {
  const p = { ...DEFAULT_PARAMS, ...params };
  const salary = parseFloat(emp.salary_s || 0);
  const { score, round, status } = pickScore(emp, p.roundMode);
  const hasAttendance = emp.att_id !== null && emp.att_id !== undefined;

  const sickDays = parseFloat(emp.sick_leave_days || 0);
  const maternityDays = parseFloat(emp.maternity_leave_days || 0);
  const personalDays = parseFloat(emp.personal_leave_days || 0);
  const personalTimes = parseInt(emp.personal_leave_times || 0);
  const lateCount = parseInt(emp.late_count || 0);
  const absentCount = parseInt(emp.absent_count || 0);

  // วันลาที่นับตามข้อ 1(8): ลาป่วย + ลากิจ + ลาคลอดส่วนเกิน 90 วัน
  // (ลาราชการทหาร/ลาพักผ่อน ไม่นับ)
  const excessMaternity = Math.max(0, maternityDays - 90);
  const totalCountedLeave = sickDays + personalDays + excessMaternity;

  const groupMax = getGroupMax(emp.name_type_group || "บริหารทั่วไป");
  let dataIssue = "";
  if (salary <= 0) dataIssue = "ไม่มีข้อมูลค่าตอบแทน";
  else if (salary >= groupMax)
    dataIssue = `ค่าตอบแทนเดิม ${salary} ≥ ขั้นสูงกลุ่มงาน ${groupMax}`;

  let eligible = true;
  let ineligible_reason = "";
  const roundText = p.roundMode === "latest" ? "รอบ 1/2" : "รอบ " + p.roundMode;

  if (salary <= 0) {
    eligible = false;
    ineligible_reason = "ไม่มีข้อมูลค่าตอบแทน (salary = 0)";
  } else if (!score || score <= 0) {
    eligible = false;
    ineligible_reason = "ยังไม่มีผลการประเมิน (" + roundText + ")";
  } else if (score < 60) {
    eligible = false;
    ineligible_reason = `คะแนน ${score} ต่ำกว่าระดับดี (< 60) ข้อ 1(2)`;
  } else if (absentCount > 0) {
    eligible = false;
    ineligible_reason = `ขาดราชการ ${absentCount} ครั้ง ข้อ 1(7)`;
  } else if (totalCountedLeave > 45) {
    eligible = false;
    ineligible_reason = `วันลา ${totalCountedLeave} วัน เกิน 45 วัน ข้อ 1(8)`;
  } else if (personalTimes > 14) {
    eligible = false;
    ineligible_reason = `ลากิจ ${personalTimes} ครั้ง เกิน 14 ครั้ง ข้อ 1(9)`;
  } else if (lateCount > 18) {
    eligible = false;
    ineligible_reason = `มาสาย ${lateCount} ครั้ง เกิน 18 ครั้ง ข้อ 1(9)`;
  }

  return {
    order,
    id_card: String(emp.id_card || ""),
    college_code: emp.college_code || "",
    full_name: `${emp.title_s || ""}${emp.frist_name || ""} ${emp.last_name ||
      ""}`.trim(),
    name_postion: emp.name_postion || "",
    name_type_group: emp.name_type_group || "บริหารทั่วไป",
    salary_s: salary,
    performance_score: score,
    score_round: round,
    assessment_status: status,
    has_attendance: hasAttendance,
    total_counted_leave: totalCountedLeave,
    personal_leave_times: personalTimes,
    late_count: lateCount,
    absent_count: absentCount,
    data_issue: dataIssue,
    eligible,
    ineligible_reason,
    max_increase_6_percent: 0,
    individual_percentage_pre_manage: 0,
    initial_increase_pre_manage: 0,
    initial_rounded_pre_manage: 0,
    management_percentage: 0,
    actual_percentage_final: 0,
    actual_increase_amount: 0,
    actual_pct_raw: 0, // % เลื่อนจริงก่อนปัดเศษ
    actual_increase_raw: 0, // เลื่อนจริงก่อนปัดเศษ (บาท)
    final_rounded: 0,
    new_salary: salary,
    capped: false,
    pct_capped: false
  };
}

// ปัดเศษ + ควบคุมวงเงิน + กระจายเงินที่เหลือจากคนถึงเพดาน (เทียบ applyRoundingAndBudgetControl)
function applyRoundingAndBudgetControl(eligible, totalBudget, maxPct) {
  const MAX_ITER = 20;
  const budgetPerMonth = totalBudget / 12;

  const calcFinal = (emp, pct) => {
    const rawInc = emp.salary_s * (pct / 100);
    let inc = roundUpTen(rawInc);
    let newSal = emp.salary_s + inc;
    let capped = false;
    const maxSal = getGroupMax(emp.name_type_group);
    if (newSal > maxSal) {
      inc = roundUpTen(Math.max(0, maxSal - emp.salary_s));
      // ไม่ให้ติดลบเมื่อค่าตอบแทนเดิมเกินขั้นสูงอยู่แล้ว (ส่วนต่างจากหน้าสถานศึกษา)
      inc = Math.max(0, Math.min(inc, maxSal - emp.salary_s));
      newSal = emp.salary_s + inc;
      capped = true;
    }
    return { inc, newSal, capped };
  };

  eligible.forEach(emp => {
    const r = calcFinal(emp, emp.actual_percentage_final);
    emp.final_rounded = r.inc;
    emp.new_salary = r.newSal;
    emp.capped = r.capped;
  });

  for (let iter = 0; iter < MAX_ITER; iter++) {
    const usedMo = eligible.reduce((s, e) => s + e.final_rounded, 0);
    const remainMo = budgetPerMonth - usedMo;
    if (remainMo < 10) break;
    const nonCapped = eligible.filter(e => !e.capped);
    if (nonCapped.length === 0) break;

    const nonCappedSalary = nonCapped.reduce((s, e) => s + e.salary_s, 0);
    let redistributed = 0;

    nonCapped.forEach(emp => {
      const addMo = remainMo * (emp.salary_s / nonCappedSalary);
      const addRounded = roundUpTen(addMo);
      const tryInc = emp.final_rounded + addRounded;
      const tryNewSal = emp.salary_s + tryInc;
      const maxSal = getGroupMax(emp.name_type_group);

      if (tryNewSal <= maxSal) {
        emp.final_rounded = tryInc;
        emp.new_salary = tryNewSal;
        redistributed += addRounded;
      } else {
        const capInc = Math.max(0, maxSal - emp.salary_s);
        redistributed += capInc - emp.final_rounded;
        emp.final_rounded = capInc;
        emp.new_salary = emp.salary_s + capInc;
        emp.capped = true;
      }
    });

    if (redistributed <= 0) break;
  }

  // ยังเกินวงเงิน → ลดรายที่ได้มากสุดทีละ 10 บาท (ข้อ 1(5))
  let totalYearFinal = eligible.reduce((s, e) => s + e.final_rounded * 12, 0);
  let cutIter = 0;
  while (totalYearFinal > totalBudget && cutIter < 500) {
    const maxEmp = eligible
      .filter(e => !e.capped && e.final_rounded >= 10)
      .reduce((m, c) => (c.final_rounded > m.final_rounded ? c : m), {
        final_rounded: -1
      });
    if (maxEmp.final_rounded < 0) break;
    maxEmp.final_rounded -= 10;
    maxEmp.new_salary -= 10;
    totalYearFinal -= 120;
    cutIter++;
  }

  // เพดานรายบุคคล maxPct% ปัดขึ้นหลักสิบ ข้อ 1(2) — หน้าสถานศึกษาไม่ได้ตรวจหลังขั้นกระจาย
  // ตัดส่วนเกินออกโดยไม่นำไปกระจายต่อ (วงเงินส่วนนั้นไม่ถูกใช้)
  eligible.forEach(emp => {
    const pctCapInc = roundUpTen(emp.salary_s * (maxPct / 100));
    if (emp.final_rounded > pctCapInc) {
      emp.final_rounded = pctCapInc;
      emp.new_salary = emp.salary_s + pctCapInc;
      emp.pct_capped = true;
    }
  });
}

/**
 * คำนวณทั้งสถานศึกษา (เทียบ calcTotalSalary + calculateCompensation)
 * @param {Array} employees แถวดิบจาก API ของสถานศึกษาเดียว
 * @param {Object} params { budgetPct, maxPct, roundMode }
 * @returns {{ records: Array, summary: Object }}
 */
export function calculateCollege(employees, params) {
  const p = { ...DEFAULT_PARAMS, ...params };
  const records = (employees || []).map((emp, i) => buildRecord(emp, p, i + 1));

  const totalSalary = records.reduce((s, e) => s + e.salary_s, 0);
  // วงเงิน = % × ฐาน × 12 เดือน (ข้อ 1(3))
  const totalBudget = totalSalary * (p.budgetPct / 100) * 12;

  const eligible = records.filter(e => e.eligible);
  const totalScore = eligible.reduce((s, e) => s + e.performance_score, 0);

  if (eligible.length > 0 && totalScore > 0) {
    // ขั้นที่ 1-4: ขั้นต้น
    eligible.forEach(emp => {
      emp.max_increase_6_percent = parseFloat(
        (emp.salary_s * (p.maxPct / 100)).toFixed(2)
      );
      emp.individual_percentage_pre_manage = parseFloat(
        ((p.maxPct * emp.performance_score) / totalScore).toFixed(3)
      );
      emp.initial_increase_pre_manage = parseFloat(
        (emp.salary_s * (emp.individual_percentage_pre_manage / 100)).toFixed(2)
      );
      emp.initial_rounded_pre_manage = roundUpTen(
        emp.initial_increase_pre_manage
      );
    });

    // ขั้นที่ 5-6: วงเงินคงเหลือ → ร้อยละบริหาร (เท่ากันทุกคน)
    const sumInitialRounded = eligible.reduce(
      (s, e) => s + e.initial_rounded_pre_manage,
      0
    );
    const remainingBudgetForMgmt = totalBudget - sumInitialRounded * 12;
    const eligibleSalarySum = eligible.reduce((s, e) => s + e.salary_s, 0);
    const mgmtPctPerPerson = parseFloat(
      ((remainingBudgetForMgmt / 12 / eligibleSalarySum) * 100).toFixed(3)
    );

    // ขั้นที่ 7-8
    eligible.forEach(emp => {
      emp.management_percentage = mgmtPctPerPerson;
      const rawActualPct =
        emp.individual_percentage_pre_manage + emp.management_percentage;
      emp.actual_percentage_final = parseFloat(
        Math.min(rawActualPct, p.maxPct).toFixed(3)
      );
      emp.actual_increase_amount = parseFloat(
        (emp.salary_s * (emp.actual_percentage_final / 100)).toFixed(2)
      );
      // เก็บค่าก่อนปัดเศษไว้ตรวจสอบ (ขั้นที่ 9 จะเขียนทับด้วยผลหลังปัด/กระจาย)
      emp.actual_pct_raw = emp.actual_percentage_final;
      emp.actual_increase_raw = emp.actual_increase_amount;
    });

    applyRoundingAndBudgetControl(eligible, totalBudget, p.maxPct);

    // ขั้นที่ 9: % จริงหลัง redistribute
    eligible.forEach(emp => {
      emp.actual_increase_amount = emp.final_rounded;
      emp.actual_percentage_final = parseFloat(
        ((emp.final_rounded / emp.salary_s) * 100).toFixed(3)
      );
    });
  }

  const distributedMonth = eligible.reduce((s, e) => s + e.final_rounded, 0);
  const summary = {
    personnel_count: records.length,
    eligible_count: eligible.length,
    ineligible_count: records.length - eligible.length,
    no_score_count: records.filter(e => !(e.performance_score > 0)).length,
    unapproved_score_count: records.filter(
      e => e.performance_score > 0 && e.assessment_status !== "approved"
    ).length,
    no_attendance_count: records.filter(e => !e.has_attendance).length,
    data_issue_count: records.filter(e => e.data_issue).length,
    capped_count: eligible.filter(e => e.capped).length,
    pct_capped_count: eligible.filter(e => e.pct_capped).length,
    total_salary: totalSalary,
    eligible_salary: eligible.reduce((s, e) => s + e.salary_s, 0),
    budget_year: parseFloat(totalBudget.toFixed(2)),
    distributed_month: distributedMonth,
    distributed_year: distributedMonth * 12,
    new_salary_total: records.reduce((s, e) => s + e.new_salary, 0)
  };

  return { records, summary };
}
